import { auth } from './stores/auth.svelte';

export class ApiError extends Error {
    constructor(public status: number, message: string, public data?: any) {
        super(message);
    }
}

const RAW_BASE = import.meta.env.PUBLIC_API_BASE_URL ?? '';

/**
 * Normalize the API base URL.
 * - Empty → use relative URLs (dev, with Vite proxy).
 * - Has scheme (http:// or https://) → use as absolute base.
 * - Missing scheme → assume https:// (prevents the "treated as path" bug).
 */
function normalizeBase(raw: string): string {
    const trimmed = raw.trim().replace(/\/+$/, '');
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    // Coolify sometimes ships FQDN without scheme — assume https in production
    return `https://${trimmed}`;
}

function inferProductionBase(): string {
    if (typeof window === 'undefined') return '';

    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
        return '';
    }

    // The production frontend is served from keys.sprezzatura.local while the API
    // is served from keys-backend.sprezzatura.local. If Coolify does not inject a
    // build-time PUBLIC_API_BASE_URL, relative /api URLs hit the static nginx
    // SPA fallback and return index.html with HTTP 200. That string then flows
    // into stores that expect arrays and crashes on `.find()`.
    if (hostname === 'keys.sprezzatura.local') {
        return `${protocol}//keys-backend.sprezzatura.local`;
    }

    return '';
}

const API_BASE = normalizeBase(RAW_BASE) || inferProductionBase();

if (RAW_BASE && !API_BASE.startsWith('http')) {
    console.error('[api] PUBLIC_API_BASE_URL is malformed:', RAW_BASE);
}

function buildUrl(path: string): string {
    if (!API_BASE) return path; // dev: relative, vite proxies it
    const p = path.startsWith('/') ? path : `/${path}`;
    if (API_BASE.endsWith('/api') && p.startsWith('/api/')) {
        return `${API_BASE}${p.slice('/api'.length)}`;
    }
    return `${API_BASE}${p}`;
}

export async function api<T = any>(
    path: string,
    options: RequestInit = {}
): Promise<T> {
    const headers = new Headers(options.headers);
    if (options.body) {
        headers.set('Content-Type', 'application/json');
    }
    if (auth.token) headers.set('Authorization', `Bearer ${auth.token}`);

    const res = await fetch(buildUrl(path), { ...options, headers });

    if (res.status === 401 && auth.token) {
        auth.logout();
        throw new ApiError(401, 'Session expired');
    }

    if (res.status === 204) return undefined as T;

    let body: any = null;
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
        body = await res.json();
    } else {
        body = await res.text();
    }

    if (!res.ok) {
        let friendlyMessage = body?.message ?? `HTTP ${res.status}`;
        if (body?.issues?.fieldErrors) {
            const fieldErrors = Object.entries(body.issues.fieldErrors)
                .map(([field, errors]: [string, any]) => `${field}: ${(errors as string[]).join(', ')}`)
                .join('; ');
            if (fieldErrors) friendlyMessage = fieldErrors;
        }
        throw new ApiError(res.status, friendlyMessage, body);
    }

    if (!contentType.includes('application/json')) {
        const preview = typeof body === 'string' ? body.slice(0, 120).replace(/\s+/g, ' ') : String(body);
        throw new ApiError(
            res.status,
            `Expected JSON from ${buildUrl(path)}, got ${contentType || 'unknown content type'}: ${preview}`,
            body
        );
    }

    return body as T;
}